import React, { useEffect, useState } from 'react';

import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Field } from '@buildoutinc/blueprint-react/ui/Field';

import { Alert } from 'components/ui/alert';
import { getRaw } from 'helpers/api';

import MarketPicker from './market_picker';
import type { MarketOption } from './types';

interface Props {
  companyId: string | null;
  // The market as the page detail form holds it, saved with the page's other changes.
  marketCode: string | null;
  marketsUrl: string;
  // False for a page in the parent company's collection, which only the parent company can retarget.
  isOwned: boolean;
  onChange: (marketCode: string | null) => void;
}

const CENSUS_NOTE = 'Census metro and micro areas from the OMB July 2023 delineations, plus each divided metro ' +
  'as a whole.';
const PARENT_COMPANY_NOTE = 'Your parent company manages this page, so only it can change the market.';

const targetSentence = (market: MarketOption | undefined, isOwned: boolean) => {
  if (!market && !isOwned) return "No market yet, so this page won't fill the slot until your parent company adds one.";
  if (!market) return "No market yet, so this page won't fill the slot. Add a market to start using it.";

  return `Fills the slot for properties in ${market.title}.`;
};

export default function MarketTargetingCard({ companyId, marketCode, marketsUrl, isOwned, onChange }: Props) {
  const [markets, setMarkets] = useState<MarketOption[] | null>(null);
  const [hasLoadError, setHasLoadError] = useState(false);

  // The Census list is fixed for the page's life, so it loads once.
  useEffect(() => {
    const load = async () => {
      try {
        const response: { markets: MarketOption[] } = await getRaw(
          `${marketsUrl}${companyId ? `?company_id=${companyId}` : ''}`,
        );
        setMarkets(response.markets);
      } catch {
        setHasLoadError(true);
      }
    };

    load();
  }, [marketsUrl, companyId]);

  const market = markets?.find(option => option.code === marketCode);

  return (
    <Card className="mb-4">
      <Card.Body>
        <h5 className="mb-1">Market targeting</h5>
        <p className="text-muted mb-3">The market that decides which properties get this page.</p>
        {hasLoadError ? (
          <Alert severity="destructive">
            <Alert.Title>The Census markets could not be loaded. Reload the page to try again.</Alert.Title>
          </Alert>
        ) : (
          <Field className="mb-2" style={{ maxWidth: '640px' }}>
            <Field.Label htmlFor="market-targeting-market">Market</Field.Label>
            <MarketPicker
              inputId="market-targeting-market"
              isDisabled={!isOwned || !markets}
              isLoading={!markets}
              markets={markets ?? []}
              value={marketCode}
              onChange={onChange}
            />
            <Field.Description>{isOwned ? CENSUS_NOTE : PARENT_COMPANY_NOTE}</Field.Description>
          </Field>
        )}
        {markets && <p className="mb-0">{targetSentence(market, isOwned)}</p>}
      </Card.Body>
    </Card>
  );
}
